/**
 * Book — customer-facing booking flow.
 *
 * Steps shipped so far:
 *   PR #9   trip picker (this file's TripPickerStep)
 *   PR #10  date picker (this file's DateStep + react-day-picker)
 *
 * Coming:
 *   PR #11  Party size + add-ons
 *   PR #12  Contact info + waiver
 *   PR #13  Stripe Payment Intent + confirmation
 *
 * State is component-local React state for v1. Will lift to useReducer
 * once 3+ steps share state through back/forward navigation.
 *
 * Deep-link support: /book?trip=sunset preselects the trip and advances
 * to the date step. Used by the marketing site's trip-card buttons.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { DayPicker } from 'react-day-picker';
import { useSearchParams } from 'react-router-dom';

import api from '../lib/api';

import 'react-day-picker/style.css';
import '../styles/calendar.css';

const STEPS = ['trip', 'date', 'party', 'addons', 'contact', 'waiver', 'pay'];

// Minimum lead time for online bookings. Matt's a pilot — he needs
// notice. Trips landing inside this window can still be taken by phone.
const MIN_LEAD_HOURS = 48;

// How far in advance can customers book? One year out is more than
// enough; longer windows make the availability query slow.
const MAX_LEAD_DAYS = 365;

// Private Charter is quoted by Captain Matt (priced by duration + scope),
// not a fixed rate. The DB keeps a flat-rate placeholder only to satisfy
// the trips_has_pricing constraint — we never show that number to the
// customer. Instead we show the marketing range and a "quoted" framing so
// the booking app matches nowherechartersllc.com. Update the range here if
// the published range changes.
const PRIVATE_QUOTE_RANGE = '$500–$1,400';
const PRIVATE_QUOTE_LABEL = `Quoted · ${PRIVATE_QUOTE_RANGE}`;

export default function Book() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [trips, setTrips] = useState([]);
  const [tripsLoading, setTripsLoading] = useState(true);
  const [tripsError, setTripsError] = useState(null);

  const [pickedTripId, setPickedTripId] = useState(null);
  const [pickedDate, setPickedDate] = useState(null);
  const [pickedGuestCount, setPickedGuestCount] = useState(null);
  // addonsConfirmed becomes true once the customer hits "Continue" on
  // the add-ons step. Selecting zero add-ons is a valid outcome, so
  // we need an explicit confirm rather than auto-advancing.
  const [addonsConfirmed, setAddonsConfirmed] = useState(false);
  const [pickedAddOns, setPickedAddOns] = useState({});

  // Lead contact details captured on the contact step, lifted here so the
  // waiver step can submit them together with the signed waiver.
  const [contactDetails, setContactDetails] = useState(null);

  // Set once the booking is successfully created — flips the whole view
  // over to the confirmation screen.
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setTripsLoading(true);
    api
      .get('/api/trips')
      .then((res) => {
        if (cancelled) return;
        setTrips(res.data?.trips || []);
        setTripsError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setTripsError(
          err?.response?.data?.error ||
            err?.message ||
            'Could not load trips. Please try again.',
        );
      })
      .finally(() => {
        if (!cancelled) setTripsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (pickedTripId || trips.length === 0) return;
    const slug = searchParams.get('trip');
    if (!slug) return;
    const match = trips.find((t) => t.slug === slug);
    if (match) setPickedTripId(match.id);
  }, [trips, searchParams, pickedTripId]);

  const pickedTrip = useMemo(
    () => trips.find((t) => t.id === pickedTripId) || null,
    [trips, pickedTripId],
  );

  // Step progression based on what's set so far.
  let currentStep = 'trip';
  if (pickedTrip && !pickedDate) currentStep = 'date';
  if (pickedTrip && pickedDate && !pickedGuestCount) currentStep = 'party';
  if (pickedTrip && pickedDate && pickedGuestCount && !addonsConfirmed)
    currentStep = 'addons';
  if (pickedTrip && pickedDate && pickedGuestCount && addonsConfirmed)
    currentStep = 'contact';
  if (
    pickedTrip &&
    pickedDate &&
    pickedGuestCount &&
    addonsConfirmed &&
    contactDetails
  )
    currentStep = 'waiver';
  const stepIndex = STEPS.indexOf(currentStep);

  const handlePickTrip = (trip) => {
    setPickedTripId(trip.id);
    setPickedDate(null);
    setPickedGuestCount(null);
    setAddonsConfirmed(false);
    setPickedAddOns({});
    setContactDetails(null);
    setSearchParams({ trip: trip.slug }, { replace: true });
  };

  const handleChangeTrip = () => {
    setPickedTripId(null);
    setPickedDate(null);
    setPickedGuestCount(null);
    setAddonsConfirmed(false);
    setPickedAddOns({});
    setContactDetails(null);
    setSearchParams({}, { replace: true });
  };

  const handlePickDate = (date) => {
    setPickedDate(toYmd(date));
    setPickedGuestCount(null);
    setAddonsConfirmed(false);
    setPickedAddOns({});
    setContactDetails(null);
  };

  const handleChangeDate = () => {
    setPickedDate(null);
    setPickedGuestCount(null);
    setAddonsConfirmed(false);
    setPickedAddOns({});
    setContactDetails(null);
  };

  const handlePickGuestCount = (n) => {
    setPickedGuestCount(n);
    setAddonsConfirmed(false);
    setPickedAddOns({});
    setContactDetails(null);
  };

  const handleChangeGuestCount = () => {
    setPickedGuestCount(null);
    setAddonsConfirmed(false);
    setPickedAddOns({});
    setContactDetails(null);
  };

  const handleConfirmAddOns = (selection) => {
    setPickedAddOns(selection);
    setAddonsConfirmed(true);
  };

  const handleChangeAddOns = () => {
    setAddonsConfirmed(false);
    setContactDetails(null);
  };

  return (
    <>
      <Helmet>
        <title>Reserve your charter — Nowhere Charters</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <main
        style={{
          maxWidth: '900px',
          margin: '0 auto',
          padding: '2rem 1.5rem 4rem',
        }}
      >
        {booking ? (
          <ConfirmationStep
            booking={booking}
            pickedTrip={pickedTrip}
            pickedDate={pickedDate}
            pickedGuestCount={pickedGuestCount}
          />
        ) : (
          <>
            <ProgressBar stepIndex={stepIndex} />

            {currentStep === 'trip' ? (
              <TripPickerStep
                trips={trips}
                loading={tripsLoading}
                error={tripsError}
                onPick={handlePickTrip}
              />
            ) : currentStep === 'date' ? (
              <DateStep
                pickedTrip={pickedTrip}
                onPickDate={handlePickDate}
                onChangeTrip={handleChangeTrip}
              />
            ) : currentStep === 'party' ? (
              <PartyStep
                pickedTrip={pickedTrip}
                pickedDate={pickedDate}
                onPick={handlePickGuestCount}
                onChangeTrip={handleChangeTrip}
                onChangeDate={handleChangeDate}
              />
            ) : currentStep === 'addons' ? (
              <AddOnsStep
                pickedTrip={pickedTrip}
                pickedDate={pickedDate}
                pickedGuestCount={pickedGuestCount}
                onConfirm={handleConfirmAddOns}
                onChangeTrip={handleChangeTrip}
                onChangeDate={handleChangeDate}
                onChangeGuestCount={handleChangeGuestCount}
              />
            ) : currentStep === 'contact' ? (
              <ContactStep
                pickedTrip={pickedTrip}
                pickedDate={pickedDate}
                pickedGuestCount={pickedGuestCount}
                pickedAddOns={pickedAddOns}
                initial={contactDetails}
                onChangeTrip={handleChangeTrip}
                onChangeDate={handleChangeDate}
                onChangeGuestCount={handleChangeGuestCount}
                onChangeAddOns={handleChangeAddOns}
                onContinue={setContactDetails}
              />
            ) : (
              <WaiverStep
                pickedTrip={pickedTrip}
                pickedDate={pickedDate}
                pickedGuestCount={pickedGuestCount}
                pickedAddOns={pickedAddOns}
                contact={contactDetails}
                onChangeTrip={handleChangeTrip}
                onChangeDate={handleChangeDate}
                onChangeGuestCount={handleChangeGuestCount}
                onChangeAddOns={handleChangeAddOns}
                onChangeContact={() => setContactDetails(null)}
                onBooked={setBooking}
              />
            )}
          </>
        )}
      </main>
    </>
  );
}

// ---------------------------------------------------------------------------
// Progress bar (top of every booking step)
// ---------------------------------------------------------------------------
const STEP_LABELS = {
  trip: 'Trip',
  date: 'Date',
  party: 'Party',
  addons: 'Extras',
  contact: 'Contact',
  waiver: 'Waiver',
  pay: 'Pay',
};

function ProgressBar({ stepIndex }) {
  return (
    <ol
      style={{
        listStyle: 'none',
        margin: 0,
        marginBottom: '2rem',
        padding: 0,
        display: 'grid',
        gridTemplateColumns: `repeat(${STEPS.length}, 1fr)`,
        gap: '0.5rem',
      }}
    >
      {STEPS.map((step, i) => {
        const isActive = i === stepIndex;
        const isDone = i < stepIndex;
        return (
          <li
            key={step}
            aria-current={isActive ? 'step' : undefined}
            style={{
              padding: '0.5rem 0.25rem',
              textAlign: 'center',
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: isActive
                ? '#0D2351'
                : isDone
                ? '#0D2351'
                : 'rgba(0,0,0,0.4)',
              borderBottom: `2px solid ${
                isActive ? '#F6C300' : isDone ? '#0D2351' : 'rgba(0,0,0,0.1)'
              }`,
            }}
          >
            {`${i + 1}. ${STEP_LABELS[step]}`}
          </li>
        );
      })}
    </ol>
  );
}

// ---------------------------------------------------------------------------
// Step 1 — trip picker
// ---------------------------------------------------------------------------
function TripPickerStep({ trips, loading, error, onPick }) {
  return (
    <section>
      <h1 style={{ marginTop: 0, fontSize: '1.75rem' }}>
        Which trip are you booking?
      </h1>
      <p
        style={{
          marginTop: '0.5rem',
          marginBottom: '2rem',
          fontSize: '1rem',
          color: 'rgba(0,0,0,0.65)',
        }}
      >
        Six options. Pick the one that fits — pricing and details below
        each card.
      </p>

      {loading ? (
        <p>Loading trips…</p>
      ) : error ? (
        <p style={{ color: '#9F1239' }}>{error}</p>
      ) : trips.length === 0 ? (
        <p>No active trips right now. Please call Captain Matt directly.</p>
      ) : (
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '1rem',
          }}
        >
          {trips.map((t) => (
            <TripCard key={t.id} trip={t} onPick={() => onPick(t)} />
          ))}
        </ul>
      )}
    </section>
  );
}

function TripCard({ trip, onPick }) {
  return (
    <li>
      <button
        type="button"
        onClick={onPick}
        data-testid={`trip-card-${trip.slug}`}
        style={{
          width: '100%',
          textAlign: 'left',
          padding: '1.25rem',
          background: '#FFFFFF',
          border: '1px solid rgba(0,0,0,0.1)',
          borderRadius: '12px',
          cursor: 'pointer',
          transition: 'all 0.15s ease',
          fontFamily: 'inherit',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = '#F6C300';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = 'rgba(0,0,0,0.1)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        <h3
          style={{
            margin: 0,
            marginBottom: '0.25rem',
            fontSize: '1.15rem',
            color: '#0D2351',
          }}
        >
          {trip.name}
        </h3>
        <p
          style={{
            margin: 0,
            marginBottom: '0.75rem',
            fontSize: '0.85rem',
            color: 'rgba(0,0,0,0.65)',
            lineHeight: 1.4,
          }}
        >
          {trip.short_description}
        </p>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            fontSize: '0.85rem',
          }}
        >
          <span style={{ color: 'rgba(0,0,0,0.6)' }}>
            {formatDuration(trip.duration_minutes)} · up to {trip.max_guests}
          </span>
          <span style={{ fontWeight: 700, color: '#0D2351' }}>
            {priceLabel(trip)}
          </span>
        </div>
      </button>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Step 2 — date picker
// ---------------------------------------------------------------------------
function DateStep({ pickedTrip, onPickDate, onChangeTrip }) {
  // Visible month + a 90-day forward buffer drives the availability fetch.
  const [visibleMonth, setVisibleMonth] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d;
  });
  const [blocked, setBlocked] = useState(new Set());
  const [blockedLoading, setBlockedLoading] = useState(true);
  const [blockedError, setBlockedError] = useState(null);

  // Window we ask the server for: the visible month plus the two months
  // surrounding it (so paging is instant via cache).
  const windowFrom = useMemo(() => {
    const d = new Date(visibleMonth);
    d.setMonth(d.getMonth() - 1);
    d.setDate(1);
    return toYmd(d);
  }, [visibleMonth]);

  const windowTo = useMemo(() => {
    const d = new Date(visibleMonth);
    d.setMonth(d.getMonth() + 2);
    d.setDate(0); // last day of the month BEFORE — i.e. last day of +1 month
    return toYmd(d);
  }, [visibleMonth]);

  useEffect(() => {
    let cancelled = false;
    setBlockedLoading(true);
    api
      .get('/api/availability', { params: { from: windowFrom, to: windowTo } })
      .then((res) => {
        if (cancelled) return;
        setBlocked(new Set(res.data?.blocked || []));
        setBlockedError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setBlockedError(
          err?.response?.data?.error ||
            err?.message ||
            'Could not load availability.',
        );
      })
      .finally(() => {
        if (!cancelled) setBlockedLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [windowFrom, windowTo]);

  // Boundaries for the calendar itself.
  const minDate = useMemo(() => {
    const d = new Date();
    d.setHours(d.getHours() + MIN_LEAD_HOURS);
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const maxDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + MAX_LEAD_DAYS);
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  // Tell react-day-picker which dates to disable.
  const disabled = useMemo(
    () => [
      { before: minDate },
      { after: maxDate },
      (date) => blocked.has(toYmd(date)),
    ],
    [minDate, maxDate, blocked],
  );

  return (
    <section>
      <SelectionBar
        items={[
          { label: 'Trip', value: pickedTrip?.name, onChange: onChangeTrip },
        ]}
      />

      <h1 style={{ marginTop: '1.5rem', fontSize: '1.5rem' }}>Pick a date</h1>
      <p
        style={{
          marginTop: '0.25rem',
          marginBottom: '1.5rem',
          fontSize: '0.95rem',
          color: 'rgba(0,0,0,0.65)',
        }}
      >
        We accept online bookings at least 48 hours out. For sooner trips,
        call Captain Matt at{' '}
        <a href="tel:+19042439044" style={{ color: '#0D2351' }}>
          904-243-9044
        </a>
        .
      </p>

      {blockedError ? (
        <p
          role="alert"
          style={{
            padding: '0.75rem 1rem',
            background: '#FEF2F2',
            color: '#9F1239',
            borderRadius: '8px',
            fontSize: '0.9rem',
          }}
        >
          {blockedError}
        </p>
      ) : null}

      <div
        style={{
          padding: '0.5rem',
          background: '#FFFFFF',
          border: '1px solid rgba(0,0,0,0.08)',
          borderRadius: '12px',
          display: 'inline-block',
          maxWidth: '100%',
        }}
      >
        <DayPicker
          mode="single"
          selected={undefined}
          onSelect={(date) => {
            if (!date) return;
            onPickDate(date);
          }}
          month={visibleMonth}
          onMonthChange={setVisibleMonth}
          fromDate={minDate}
          toDate={maxDate}
          disabled={disabled}
          showOutsideDays
          weekStartsOn={0}
        />
        {blockedLoading ? (
          <div
            style={{
              padding: '0.4rem 0.6rem',
              fontSize: '0.8rem',
              color: 'rgba(0,0,0,0.55)',
            }}
          >
            Loading availability…
          </div>
        ) : null}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Step 3 — party size
// ---------------------------------------------------------------------------
function PartyStep({
  pickedTrip,
  pickedDate,
  onPick,
  onChangeTrip,
  onChangeDate,
}) {
  const maxGuests = pickedTrip?.max_guests || 6;
  const minGuests = pickedTrip?.min_guests_for_per_person_price || 1;
  const perPerson = pickedTrip?.price_per_person_cents;
  const flatRate = pickedTrip?.private_flat_rate_cents;

  // Local "hover" preview of pricing — guest count card the user is
  // pointing at, not necessarily the one they've picked.
  const [hoverCount, setHoverCount] = useState(null);

  // The actual picked count comes from onPick; UI keeps a transient
  // selected state until the parent moves us to the next step.
  const [selectedCount, setSelectedCount] = useState(null);

  const previewCount = selectedCount ?? hoverCount ?? minGuests;
  const isFlatRateTrip = !!flatRate && !perPerson;

  return (
    <section>
      <SelectionBar
        items={[
          { label: 'Trip', value: pickedTrip?.name, onChange: onChangeTrip },
          {
            label: 'Date',
            value: pickedDate ? formatYmd(pickedDate) : null,
            onChange: onChangeDate,
          },
        ]}
      />

      <h1 style={{ marginTop: '1.5rem', fontSize: '1.5rem' }}>
        How many guests?
      </h1>
      <p
        style={{
          marginTop: '0.25rem',
          marginBottom: '1.5rem',
          fontSize: '0.95rem',
          color: 'rgba(0,0,0,0.65)',
        }}
      >
        Up to {maxGuests} guests. The boat is yours either way — no shared
        seating with strangers.
      </p>

      {isFlatRateTrip ? (
        <FlatRateGuestPicker
          maxGuests={maxGuests}
          flatRate={flatRate}
          selected={selectedCount}
          onHover={setHoverCount}
          onSelect={(n) => {
            setSelectedCount(n);
            onPick(n);
          }}
        />
      ) : (
        <PerPersonGuestPicker
          maxGuests={maxGuests}
          minGuests={minGuests}
          perPerson={perPerson}
          selected={selectedCount}
          onHover={setHoverCount}
          onSelect={(n) => {
            setSelectedCount(n);
            onPick(n);
          }}
        />
      )}

      <PricePreview
        trip={pickedTrip}
        guestCount={previewCount}
      />
    </section>
  );
}

function PerPersonGuestPicker({
  maxGuests,
  minGuests,
  perPerson,
  selected,
  onHover,
  onSelect,
}) {
  const counts = Array.from({ length: maxGuests }, (_, i) => i + 1);
  return (
    <ul
      style={{
        listStyle: 'none',
        margin: 0,
        padding: 0,
        display: 'grid',
        gridTemplateColumns: `repeat(${maxGuests}, 1fr)`,
        gap: '0.5rem',
        marginBottom: '1.5rem',
      }}
      onMouseLeave={() => onHover(null)}
    >
      {counts.map((n) => {
        const disabled = n < minGuests;
        const isSelected = selected === n;
        return (
          <li key={n}>
            <button
              type="button"
              disabled={disabled}
              onMouseEnter={() => onHover(n)}
              onClick={() => onSelect(n)}
              aria-label={`${n} guest${n === 1 ? '' : 's'}`}
              style={{
                width: '100%',
                aspectRatio: '1',
                background: isSelected ? '#0D2351' : '#FFFFFF',
                color: isSelected ? '#FFFFFF' : disabled ? 'rgba(0,0,0,0.3)' : '#0D2351',
                border: isSelected
                  ? '2px solid #0D2351'
                  : '1px solid rgba(0,0,0,0.15)',
                borderRadius: '10px',
                fontSize: '1.25rem',
                fontWeight: 700,
                cursor: disabled ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
                transition: 'all 0.1s ease',
              }}
            >
              {n}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function FlatRateGuestPicker({
  maxGuests,
  flatRate,
  selected,
  onHover,
  onSelect,
}) {
  // Flat-rate trips don't change price by headcount, but we still capture
  // the count so the manifest is right.
  const counts = Array.from({ length: maxGuests }, (_, i) => i + 1);
  return (
    <ul
      style={{
        listStyle: 'none',
        margin: 0,
        padding: 0,
        display: 'grid',
        gridTemplateColumns: `repeat(${maxGuests}, 1fr)`,
        gap: '0.5rem',
        marginBottom: '1.5rem',
      }}
      onMouseLeave={() => onHover(null)}
    >
      {counts.map((n) => {
        const isSelected = selected === n;
        return (
          <li key={n}>
            <button
              type="button"
              onMouseEnter={() => onHover(n)}
              onClick={() => onSelect(n)}
              aria-label={`${n} guest${n === 1 ? '' : 's'}`}
              style={{
                width: '100%',
                aspectRatio: '1',
                background: isSelected ? '#0D2351' : '#FFFFFF',
                color: isSelected ? '#FFFFFF' : '#0D2351',
                border: isSelected
                  ? '2px solid #0D2351'
                  : '1px solid rgba(0,0,0,0.15)',
                borderRadius: '10px',
                fontSize: '1.25rem',
                fontWeight: 700,
                cursor: 'pointer',
                fontFamily: 'inherit',
                transition: 'all 0.1s ease',
              }}
            >
              {n}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function PricePreview({ trip, guestCount }) {
  if (!trip || !guestCount) return null;

  const perPerson = trip.price_per_person_cents;
  const flatRate = trip.private_flat_rate_cents;
  const isFlat = !!flatRate && !perPerson;

  // Private/flat-rate trips are quoted by Captain Matt, not charged a fixed
  // rate — show the marketing range instead of the DB placeholder figure.
  const total = isFlat ? null : perPerson * guestCount;
  const displayAmount = isFlat ? PRIVATE_QUOTE_RANGE : formatUsd(total);
  const lineLabel = isFlat
    ? 'Quoted by Captain Matt'
    : `${formatUsd(perPerson)} × ${guestCount} ${
        guestCount === 1 ? 'guest' : 'guests'
      }`;

  return (
    <div
      style={{
        padding: '1rem 1.25rem',
        background: '#F9FAFB',
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: '12px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '0.95rem',
          color: 'rgba(0,0,0,0.7)',
        }}
      >
        <span>{lineLabel}</span>
        <span>{displayAmount}</span>
      </div>
      <div
        style={{
          marginTop: '0.5rem',
          paddingTop: '0.5rem',
          borderTop: '1px solid rgba(0,0,0,0.08)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
        }}
      >
        <span style={{ fontWeight: 700 }}>Total</span>
        <span style={{ fontWeight: 700, fontSize: '1.25rem', color: '#0D2351' }}>
          {displayAmount}
        </span>
      </div>
      {isFlat ? (
        <p
          style={{
            margin: '0.5rem 0 0',
            fontSize: '0.8rem',
            color: 'rgba(0,0,0,0.55)',
          }}
        >
          Private Charter is quoted by Captain Matt — priced by your duration
          and route ({PRIVATE_QUOTE_RANGE}). Reserve now; he confirms the exact
          total before any charge.
        </p>
      ) : null}
      <p
        style={{
          margin: '0.5rem 0 0',
          fontSize: '0.8rem',
          color: 'rgba(0,0,0,0.55)',
        }}
      >
        Add-ons (Sunset premium, Anniversary, Photography, Private pickup,
        etc.) available — pick at checkout or add by phone afterwards.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 4 — add-ons
// ---------------------------------------------------------------------------
function AddOnsStep({
  pickedTrip,
  pickedDate,
  pickedGuestCount,
  onConfirm,
  onChangeTrip,
  onChangeDate,
  onChangeGuestCount,
}) {
  const [addOns, setAddOns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // selected: { [add_on_id]: { notes?: string } }
  const [selected, setSelected] = useState({});

  useEffect(() => {
    if (!pickedTrip?.slug) return;
    let cancelled = false;
    setLoading(true);
    api
      .get(`/api/trips/${pickedTrip.slug}/add-ons`)
      .then((res) => {
        if (cancelled) return;
        setAddOns(res.data?.add_ons || []);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err?.response?.data?.error ||
            err?.message ||
            'Could not load add-ons.',
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pickedTrip?.slug]);

  const toggle = (addOn) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[addOn.id]) {
        delete next[addOn.id];
      } else {
        next[addOn.id] = { notes: '' };
      }
      return next;
    });
  };

  const updateNotes = (addOnId, notes) => {
    setSelected((prev) => {
      if (!prev[addOnId]) return prev;
      return { ...prev, [addOnId]: { ...prev[addOnId], notes } };
    });
  };

  const selectedCount = Object.keys(selected).length;
  const baseSubtotal = baseSubtotalFor(pickedTrip, pickedGuestCount);

  // Selected add-ons with known prices contribute to a "known so far"
  // running total. NULL-priced add-ons display as "quoted" alongside.
  const knownAddOnTotal = Object.entries(selected).reduce(
    (sum, [id]) => {
      const a = addOns.find((x) => x.id === id);
      return sum + (a?.price_cents || 0);
    },
    0,
  );
  const quotedAddOnCount = Object.entries(selected).filter(([id]) => {
    const a = addOns.find((x) => x.id === id);
    return a && a.price_cents == null;
  }).length;

  return (
    <section>
      <SelectionBar
        items={[
          { label: 'Trip', value: pickedTrip?.name, onChange: onChangeTrip },
          {
            label: 'Date',
            value: pickedDate ? formatYmd(pickedDate) : null,
            onChange: onChangeDate,
          },
          {
            label: 'Guests',
            value: pickedGuestCount
              ? `${pickedGuestCount} · ${tripPriceText(pickedTrip, baseSubtotal)}`
              : null,
            onChange: onChangeGuestCount,
          },
        ]}
      />

      <h1 style={{ marginTop: '1.5rem', fontSize: '1.5rem' }}>
        Any add-ons?
      </h1>
      <p
        style={{
          marginTop: '0.25rem',
          marginBottom: '1.5rem',
          fontSize: '0.95rem',
          color: 'rgba(0,0,0,0.65)',
        }}
      >
        Optional. Most are quoted on request — Captain Matt confirms the
        details and price after you reserve. Skip with no add-ons if you
        just want the standard trip.
      </p>

      {loading ? (
        <p>Loading add-ons…</p>
      ) : error ? (
        <p style={{ color: '#9F1239' }}>{error}</p>
      ) : addOns.length === 0 ? (
        <p style={{ color: 'rgba(0,0,0,0.6)' }}>
          No add-ons available for this trip. Continue to contact info.
        </p>
      ) : (
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            marginBottom: '1.5rem',
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
          }}
        >
          {addOns.map((a) => (
            <AddOnRow
              key={a.id}
              addOn={a}
              selected={!!selected[a.id]}
              notes={selected[a.id]?.notes || ''}
              onToggle={() => toggle(a)}
              onNotesChange={(v) => updateNotes(a.id, v)}
            />
          ))}
        </ul>
      )}

      <AddOnsPreview
        baseSubtotal={baseSubtotal}
        knownAddOnTotal={knownAddOnTotal}
        quotedAddOnCount={quotedAddOnCount}
        selectedCount={selectedCount}
        isFlatRateTrip={
          !!pickedTrip?.private_flat_rate_cents &&
          !pickedTrip?.price_per_person_cents
        }
      />

      <div
        style={{
          marginTop: '1.5rem',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '0.5rem',
        }}
      >
        <button
          type="button"
          onClick={() => onConfirm({})}
          style={{
            padding: '0.65rem 1.1rem',
            background: 'transparent',
            color: '#0D2351',
            border: '1px solid rgba(13,35,81,0.25)',
            borderRadius: '8px',
            fontSize: '0.95rem',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Skip — no add-ons
        </button>
        <button
          type="button"
          onClick={() => onConfirm(selected)}
          disabled={selectedCount === 0}
          style={{
            padding: '0.65rem 1.1rem',
            background: selectedCount === 0 ? 'rgba(13,35,81,0.4)' : '#0D2351',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '8px',
            fontSize: '0.95rem',
            fontWeight: 700,
            cursor: selectedCount === 0 ? 'not-allowed' : 'pointer',
          }}
        >
          Continue
          {selectedCount > 0
            ? ` with ${selectedCount} add-on${selectedCount === 1 ? '' : 's'}`
            : ''}
        </button>
      </div>
    </section>
  );
}

function AddOnRow({ addOn, selected, notes, onToggle, onNotesChange }) {
  return (
    <li
      style={{
        padding: '0.85rem 1rem',
        background: selected ? '#FFFBEB' : '#FFFFFF',
        border: `1px solid ${selected ? '#FCD34D' : 'rgba(0,0,0,0.1)'}`,
        borderRadius: '10px',
      }}
    >
      <label
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '0.75rem',
          cursor: 'pointer',
        }}
      >
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          style={{ marginTop: '0.25rem', flexShrink: 0 }}
          data-testid={`addon-${addOn.slug}`}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              gap: '0.5rem',
            }}
          >
            <span style={{ fontWeight: 700, color: '#0D2351' }}>
              {addOn.name}
            </span>
            <span
              style={{
                fontSize: '0.85rem',
                color: 'rgba(0,0,0,0.55)',
                whiteSpace: 'nowrap',
              }}
            >
              {addOn.price_cents != null
                ? formatUsd(addOn.price_cents)
                : 'Quoted'}
            </span>
          </div>
          {addOn.short_description ? (
            <p
              style={{
                margin: '0.25rem 0 0',
                fontSize: '0.85rem',
                color: 'rgba(0,0,0,0.65)',
                lineHeight: 1.4,
              }}
            >
              {addOn.short_description}
            </p>
          ) : null}
          {selected ? (
            <input
              type="text"
              placeholder="Optional note for Captain Matt (e.g. anniversary number 10)"
              value={notes}
              maxLength={200}
              onChange={(e) => onNotesChange(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              style={{
                marginTop: '0.5rem',
                width: '100%',
                padding: '0.4rem 0.6rem',
                fontSize: '0.85rem',
                border: '1px solid rgba(0,0,0,0.15)',
                borderRadius: '6px',
                boxSizing: 'border-box',
              }}
            />
          ) : null}
        </div>
      </label>
    </li>
  );
}

function AddOnsPreview({
  baseSubtotal,
  knownAddOnTotal,
  quotedAddOnCount,
  selectedCount,
  isFlatRateTrip,
}) {
  const known = baseSubtotal + knownAddOnTotal;
  return (
    <div
      style={{
        padding: '1rem 1.25rem',
        background: '#F9FAFB',
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: '12px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '0.9rem',
          color: 'rgba(0,0,0,0.7)',
        }}
      >
        <span>Trip subtotal</span>
        <span>{isFlatRateTrip ? PRIVATE_QUOTE_RANGE : formatUsd(baseSubtotal)}</span>
      </div>
      {knownAddOnTotal > 0 ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '0.9rem',
            color: 'rgba(0,0,0,0.7)',
            marginTop: '0.25rem',
          }}
        >
          <span>Add-ons (priced)</span>
          <span>+{formatUsd(knownAddOnTotal)}</span>
        </div>
      ) : null}
      <div
        style={{
          marginTop: '0.5rem',
          paddingTop: '0.5rem',
          borderTop: '1px solid rgba(0,0,0,0.08)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
        }}
      >
        <span style={{ fontWeight: 700 }}>
          {isFlatRateTrip ? 'Quoted range' : 'Known so far'}
        </span>
        <span style={{ fontWeight: 700, fontSize: '1.15rem', color: '#0D2351' }}>
          {isFlatRateTrip ? PRIVATE_QUOTE_RANGE : formatUsd(known)}
        </span>
      </div>
      {quotedAddOnCount > 0 || isFlatRateTrip ? (
        <p
          style={{
            margin: '0.5rem 0 0',
            fontSize: '0.8rem',
            color: 'rgba(0,0,0,0.55)',
          }}
        >
          {isFlatRateTrip
            ? 'Private Charter is quoted by Captain Matt after reservation. '
            : ''}
          {quotedAddOnCount > 0
            ? `${quotedAddOnCount} add-on${
                quotedAddOnCount === 1 ? '' : 's'
              } you selected will be quoted before charge — Matt confirms the total via email or text within a day.`
            : ''}
        </p>
      ) : selectedCount === 0 ? (
        <p
          style={{
            margin: '0.5rem 0 0',
            fontSize: '0.8rem',
            color: 'rgba(0,0,0,0.55)',
          }}
        >
          Add-ons (Sunset premium, Anniversary, Photography, Private pickup,
          etc.) are optional. Skip to keep it simple.
        </p>
      ) : null}
    </div>
  );
}

function baseSubtotalFor(trip, guestCount) {
  if (!trip) return 0;
  if (trip.private_flat_rate_cents && !trip.price_per_person_cents) {
    return trip.private_flat_rate_cents;
  }
  return (trip.price_per_person_cents || 0) * (guestCount || 0);
}

// What to show as the trip's price in summaries/selection bars. Private
// Charter is quoted by Captain Matt (range), so we never surface the DB
// placeholder figure — show the marketing range instead.
function tripPriceText(trip, cents) {
  if (trip && trip.private_flat_rate_cents && !trip.price_per_person_cents) {
    return PRIVATE_QUOTE_RANGE;
  }
  return formatUsd(cents);
}

// ---------------------------------------------------------------------------
// Step 5 — contact details + create the booking
// ---------------------------------------------------------------------------
const fieldStyle = {
  width: '100%',
  padding: '0.6rem 0.75rem',
  fontSize: '1rem',
  border: '1px solid rgba(0,0,0,0.18)',
  borderRadius: '8px',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};

const labelStyle = {
  display: 'block',
  fontSize: '0.78rem',
  fontWeight: 700,
  color: 'rgba(0,0,0,0.6)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginBottom: '0.35rem',
};

function TextField({
  id,
  label,
  type = 'text',
  value,
  onChange,
  error,
  autoComplete,
  required,
  placeholder,
}) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <label htmlFor={id} style={labelStyle}>
        {label}
        {required ? ' *' : ''}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        placeholder={placeholder}
        data-testid={id}
        style={{
          ...fieldStyle,
          borderColor: error ? '#9F1239' : 'rgba(0,0,0,0.18)',
        }}
      />
      {error ? (
        <p style={{ margin: '0.3rem 0 0', color: '#9F1239', fontSize: '0.8rem' }}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

function ContactStep({
  pickedTrip,
  pickedDate,
  pickedGuestCount,
  pickedAddOns,
  initial,
  onChangeTrip,
  onChangeDate,
  onChangeGuestCount,
  onChangeAddOns,
  onContinue,
}) {
  const subtotal = baseSubtotalFor(pickedTrip, pickedGuestCount);
  const addOnIds = Object.keys(pickedAddOns || {});

  const [firstName, setFirstName] = useState(initial?.first_name || '');
  const [lastName, setLastName] = useState(initial?.last_name || '');
  const [email, setEmail] = useState(initial?.email || '');
  const [phone, setPhone] = useState(initial?.phone || '');
  const [requests, setRequests] = useState(initial?.special_requests || '');
  const [smsOptIn, setSmsOptIn] = useState(initial?.sms_opt_in || false);

  const [errors, setErrors] = useState({});

  const validate = () => {
    const next = {};
    if (!firstName.trim()) next.firstName = 'Please enter your first name.';
    if (!lastName.trim()) next.lastName = 'Please enter your last name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      next.email = 'Please enter a valid email address.';
    }
    if (phone.replace(/\D/g, '').length < 10) {
      next.phone = 'A phone number is required so Captain Matt can confirm.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleContinue = (e) => {
    e.preventDefault();
    if (!validate()) return;
    onContinue({
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      email: email.trim(),
      phone: phone.trim(),
      sms_opt_in: smsOptIn,
      special_requests: requests.trim(),
    });
  };

  return (
    <section>
      <SelectionBar
        items={[
          { label: 'Trip', value: pickedTrip?.name, onChange: onChangeTrip },
          {
            label: 'Date',
            value: pickedDate ? formatYmd(pickedDate) : null,
            onChange: onChangeDate,
          },
          {
            label: 'Guests',
            value: pickedGuestCount
              ? `${pickedGuestCount} · ${tripPriceText(pickedTrip, subtotal)}`
              : null,
            onChange: onChangeGuestCount,
          },
          {
            label: 'Extras',
            value: addOnIds.length > 0 ? `${addOnIds.length} selected` : 'None',
            onChange: onChangeAddOns,
          },
        ]}
      />

      <h1 style={{ marginTop: '1.5rem', fontSize: '1.5rem' }}>Your details</h1>
      <p
        style={{
          marginTop: '0.25rem',
          marginBottom: '1.5rem',
          fontSize: '0.95rem',
          color: 'rgba(0,0,0,0.65)',
        }}
      >
        No charge today. Captain Matt confirms your date and time, then sends a
        secure payment link — usually within a day.
      </p>

      <form onSubmit={handleContinue} noValidate>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '0 1rem',
          }}
        >
          <TextField
            id="contact-first-name"
            label="First name"
            value={firstName}
            onChange={setFirstName}
            error={errors.firstName}
            autoComplete="given-name"
            required
          />
          <TextField
            id="contact-last-name"
            label="Last name"
            value={lastName}
            onChange={setLastName}
            error={errors.lastName}
            autoComplete="family-name"
            required
          />
        </div>

        <TextField
          id="contact-email"
          label="Email"
          type="email"
          value={email}
          onChange={setEmail}
          error={errors.email}
          autoComplete="email"
          required
        />

        <TextField
          id="contact-phone"
          label="Phone"
          type="tel"
          value={phone}
          onChange={setPhone}
          error={errors.phone}
          autoComplete="tel"
          placeholder="(904) 555-0123"
          required
        />

        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="contact-requests" style={labelStyle}>
            Anything we should know? (optional)
          </label>
          <textarea
            id="contact-requests"
            value={requests}
            maxLength={1000}
            onChange={(e) => setRequests(e.target.value)}
            placeholder="Occasion, mobility needs, must-see spots, timing preferences…"
            data-testid="contact-requests"
            rows={3}
            style={{ ...fieldStyle, resize: 'vertical' }}
          />
        </div>

        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.6rem',
            marginBottom: '1.5rem',
            fontSize: '0.9rem',
            color: 'rgba(0,0,0,0.7)',
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={smsOptIn}
            onChange={(e) => setSmsOptIn(e.target.checked)}
            data-testid="contact-sms-optin"
            style={{ marginTop: '0.2rem', flexShrink: 0 }}
          />
          <span>
            Text me about this booking (date confirmation, weather updates).
            Standard message rates apply. Optional.
          </span>
        </label>

        <div
          style={{
            padding: '1rem 1.25rem',
            background: '#F9FAFB',
            border: '1px solid rgba(0,0,0,0.08)',
            borderRadius: '12px',
            marginBottom: '1.25rem',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: '0.95rem',
              color: 'rgba(0,0,0,0.7)',
            }}
          >
            <span>Trip subtotal</span>
            <span>{tripPriceText(pickedTrip, subtotal)}</span>
          </div>
          <p
            style={{
              margin: '0.5rem 0 0',
              fontSize: '0.8rem',
              color: 'rgba(0,0,0,0.55)',
            }}
          >
            Any add-ons and the final total are confirmed by Captain Matt before
            a payment link is sent. Nothing is charged now.
          </p>
        </div>

        <button
          type="submit"
          data-testid="contact-continue"
          style={{
            width: '100%',
            padding: '0.85rem 1.1rem',
            background: '#0D2351',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '10px',
            fontSize: '1.05rem',
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Continue to waiver
        </button>
        <p
          style={{
            margin: '0.75rem 0 0',
            textAlign: 'center',
            fontSize: '0.8rem',
            color: 'rgba(0,0,0,0.55)',
          }}
        >
          Next: review &amp; sign the charter waiver. No charge today.
        </p>
      </form>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Step 6 — waiver: review + sign, then create the booking
// ---------------------------------------------------------------------------

// Render the typed full legal name to a PNG data URL (the stored signature).
function renderSignaturePng(name) {
  const canvas = document.createElement('canvas');
  canvas.width = 640;
  canvas.height = 160;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#0D2351';
  ctx.font = 'italic 52px "Brush Script MT", "Segoe Script", "Snell Roundhand", cursive';
  ctx.textBaseline = 'middle';
  ctx.fillText(name || '', 24, canvas.height / 2);
  return canvas.toDataURL('image/png');
}

function adultAge(ymd) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd || '')) return null;
  const [y, m, d] = ymd.split('-').map(Number);
  const now = new Date();
  let age = now.getFullYear() - y;
  const md = now.getMonth() - (m - 1);
  if (md < 0 || (md === 0 && now.getDate() < d)) age -= 1;
  return age;
}

function WaiverParagraphs({ lines }) {
  if (!lines) return null;
  return lines.map((t, i) => (
    <p
      key={i}
      style={{
        margin: '0 0 0.6rem',
        fontSize: '0.9rem',
        lineHeight: 1.5,
        color: 'rgba(0,0,0,0.8)',
      }}
    >
      {t}
    </p>
  ));
}

function WaiverBullets({ items }) {
  if (!items || !items.length) return null;
  return (
    <ul style={{ margin: '0 0 0.6rem', paddingLeft: '1.2rem' }}>
      {items.map((t, i) => (
        <li
          key={i}
          style={{
            fontSize: '0.9rem',
            lineHeight: 1.5,
            color: 'rgba(0,0,0,0.8)',
            marginBottom: '0.2rem',
          }}
        >
          {t}
        </li>
      ))}
    </ul>
  );
}

function WaiverStep({
  pickedTrip,
  pickedDate,
  pickedGuestCount,
  pickedAddOns,
  contact,
  onChangeTrip,
  onChangeDate,
  onChangeGuestCount,
  onChangeAddOns,
  onChangeContact,
  onBooked,
}) {
  const subtotal = baseSubtotalFor(pickedTrip, pickedGuestCount);
  const addOnIds = Object.keys(pickedAddOns || {});

  const [waiver, setWaiver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [acks, setAcks] = useState({});
  const [photoChoice, setPhotoChoice] = useState(null); // true | false | null
  const [fullName, setFullName] = useState(
    contact ? `${contact.first_name} ${contact.last_name}`.trim() : '',
  );
  const [dob, setDob] = useState('');
  const [emergencyName, setEmergencyName] = useState('');
  const [emergencyPhone, setEmergencyPhone] = useState('');
  const [minors, setMinors] = useState([]); // [{ full_name, dob }]

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get('/api/waivers/current')
      .then((res) => {
        if (cancelled) return;
        setWaiver(res.data || null);
        setLoadError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(
          err?.response?.data?.error ||
            err?.message ||
            'Could not load the waiver. Please try again.',
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const requiredAckIds = waiver?.required_ack_ids || [];
  const minorAck =
    (waiver?.sections || []).find(
      (s) => s.acknowledgment && s.acknowledgment.minorOnly,
    )?.acknowledgment || null;

  const validate = () => {
    const next = {};
    const missing = requiredAckIds.filter((id) => acks[id] !== true);
    if (missing.length) next.acks = 'Please check every required box below.';
    if (photoChoice !== true && photoChoice !== false) {
      next.photo = 'Please choose a photo/media option (Section 13).';
    }
    if (!fullName.trim()) next.fullName = 'Type your full legal name to sign.';
    const age = adultAge(dob);
    if (age == null) next.dob = 'Enter a valid date of birth.';
    else if (age < 18) {
      next.dob =
        'The person signing must be 18+. For a minor, a guardian signs at the dock — call Captain Matt.';
    }
    if (minors.length > 0) {
      if (
        minors.some(
          (m) => !m.full_name.trim() || !/^\d{4}-\d{2}-\d{2}$/.test(m.dob),
        )
      ) {
        next.minors = 'Each minor needs a full legal name and date of birth.';
      }
      if (acks.ack_minor !== true) {
        next.minorAck =
          'Check the parent/guardian box (Section 12) for the minor(s) you added.';
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    if (!validate()) return;

    setSubmitting(true);
    try {
      const payload = {
        trip_slug: pickedTrip?.slug,
        charter_date: pickedDate,
        guest_count: pickedGuestCount,
        contact: {
          first_name: contact.first_name,
          last_name: contact.last_name,
          email: contact.email,
          phone: contact.phone,
        },
        add_ons: addOnIds.map((id) => ({
          id,
          notes: pickedAddOns[id]?.notes || '',
        })),
        sms_opt_in: !!contact.sms_opt_in,
        special_requests: contact.special_requests || '',
        waiver: {
          full_legal_name: fullName.trim(),
          dob,
          emergency_contact_name: emergencyName.trim(),
          emergency_contact_phone: emergencyPhone.trim(),
          acknowledgments: acks,
          photo_release: photoChoice,
          signature_image: renderSignaturePng(fullName.trim()),
          accepted_version: waiver.version,
          minors: minors.map((m) => ({
            full_name: m.full_name.trim(),
            dob: m.dob,
          })),
        },
      };
      const res = await api.post('/api/bookings', payload);
      onBooked(
        res.data?.booking || { booking_number: '—', email: contact.email },
      );
    } catch (err) {
      setSubmitError(
        err?.response?.data?.error ||
          err?.message ||
          'Something went wrong creating your reservation. Please try again, or call Captain Matt at 904-243-9044.',
      );
      setSubmitting(false);
    }
  };

  return (
    <section>
      <SelectionBar
        items={[
          { label: 'Trip', value: pickedTrip?.name, onChange: onChangeTrip },
          {
            label: 'Date',
            value: pickedDate ? formatYmd(pickedDate) : null,
            onChange: onChangeDate,
          },
          {
            label: 'Guests',
            value: pickedGuestCount
              ? `${pickedGuestCount} · ${tripPriceText(pickedTrip, subtotal)}`
              : null,
            onChange: onChangeGuestCount,
          },
          {
            label: 'Extras',
            value: addOnIds.length > 0 ? `${addOnIds.length} selected` : 'None',
            onChange: onChangeAddOns,
          },
          {
            label: 'Contact',
            value: contact ? `${contact.first_name} ${contact.last_name}` : null,
            onChange: onChangeContact,
          },
        ]}
      />

      <h1 style={{ marginTop: '1.5rem', fontSize: '1.5rem' }}>
        Review &amp; sign the charter waiver
      </h1>
      <p
        style={{
          marginTop: '0.25rem',
          marginBottom: '1.5rem',
          fontSize: '0.95rem',
          color: 'rgba(0,0,0,0.65)',
        }}
      >
        Required to complete your reservation. Read it through, check each box,
        and sign at the bottom.
      </p>

      {loading ? (
        <p>Loading the waiver…</p>
      ) : loadError ? (
        <p role="alert" style={{ color: '#9F1239' }}>
          {loadError}
        </p>
      ) : waiver ? (
        <form onSubmit={handleSubmit} noValidate>
          <div
            style={{
              maxHeight: '420px',
              overflowY: 'auto',
              padding: '1.25rem',
              background: '#FFFFFF',
              border: '1px solid rgba(0,0,0,0.15)',
              borderRadius: '12px',
              marginBottom: '1.25rem',
            }}
          >
            <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.05rem', color: '#0D2351' }}>
              {waiver.operator}
            </h2>
            <p style={{ margin: '0 0 1rem', fontWeight: 700, color: '#0D2351' }}>
              {waiver.title}
            </p>
            <WaiverParagraphs lines={waiver.intro} />

            {(waiver.sections || []).map((s) => (
              <div key={s.n} style={{ marginTop: '1.1rem' }}>
                <h3
                  style={{
                    margin: '0 0 0.4rem',
                    fontSize: '0.95rem',
                    color: '#0D2351',
                  }}
                >
                  {s.n}. {s.heading}
                </h3>
                <WaiverParagraphs lines={s.body} />
                <WaiverBullets items={s.bullets} />
                <WaiverParagraphs lines={s.after} />

                {s.acknowledgment && !s.acknowledgment.minorOnly ? (
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.6rem',
                      padding: '0.6rem 0.75rem',
                      background: acks[s.acknowledgment.id] ? '#FFFBEB' : '#F9FAFB',
                      border: `1px solid ${
                        acks[s.acknowledgment.id] ? '#FCD34D' : 'rgba(0,0,0,0.12)'
                      }`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '0.88rem',
                      color: 'rgba(0,0,0,0.85)',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={!!acks[s.acknowledgment.id]}
                      data-testid={`waiver-ack-${s.acknowledgment.id}`}
                      onChange={(e) =>
                        setAcks((prev) => ({
                          ...prev,
                          [s.acknowledgment.id]: e.target.checked,
                        }))
                      }
                      style={{ marginTop: '0.15rem', flexShrink: 0 }}
                    />
                    <span>{s.acknowledgment.label}</span>
                  </label>
                ) : null}

                {s.choice ? (
                  <div
                    style={{
                      padding: '0.6rem 0.75rem',
                      background: '#F9FAFB',
                      border: '1px solid rgba(0,0,0,0.12)',
                      borderRadius: '8px',
                    }}
                  >
                    <p
                      style={{
                        margin: '0 0 0.4rem',
                        fontSize: '0.85rem',
                        fontWeight: 700,
                        color: 'rgba(0,0,0,0.6)',
                      }}
                    >
                      {s.choice.prompt}
                    </p>
                    {[
                      { val: true, label: s.choice.agreeLabel },
                      { val: false, label: s.choice.disagreeLabel },
                    ].map((opt) => (
                      <label
                        key={String(opt.val)}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '0.6rem',
                          marginBottom: '0.3rem',
                          fontSize: '0.88rem',
                          color: 'rgba(0,0,0,0.85)',
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          type="radio"
                          name={s.choice.id}
                          checked={photoChoice === opt.val}
                          data-testid={`waiver-photo-${opt.val ? 'agree' : 'decline'}`}
                          onChange={() => setPhotoChoice(opt.val)}
                          style={{ marginTop: '0.15rem', flexShrink: 0 }}
                        />
                        <span>{opt.label}</span>
                      </label>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </div>

          <div style={{ marginBottom: '1.25rem' }}>
            <h3 style={{ margin: '0 0 0.4rem', fontSize: '1.05rem', color: '#0D2351' }}>
              Minors in your party
            </h3>
            <p
              style={{
                margin: '0 0 0.75rem',
                fontSize: '0.85rem',
                color: 'rgba(0,0,0,0.6)',
              }}
            >
              Anyone under 18? Add them and you&rsquo;ll sign as their
              parent/guardian. Leave empty if your whole party is adults.
            </p>

            {minors.map((m, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  gap: '0.5rem',
                  alignItems: 'flex-start',
                  marginBottom: '0.5rem',
                }}
              >
                <input
                  type="text"
                  value={m.full_name}
                  maxLength={200}
                  placeholder="Minor full legal name"
                  data-testid={`minor-name-${idx}`}
                  onChange={(e) =>
                    setMinors((prev) =>
                      prev.map((x, i) =>
                        i === idx ? { ...x, full_name: e.target.value } : x,
                      ),
                    )
                  }
                  style={{ ...fieldStyle, flex: 2 }}
                />
                <input
                  type="date"
                  value={m.dob}
                  data-testid={`minor-dob-${idx}`}
                  onChange={(e) =>
                    setMinors((prev) =>
                      prev.map((x, i) =>
                        i === idx ? { ...x, dob: e.target.value } : x,
                      ),
                    )
                  }
                  style={{ ...fieldStyle, flex: 1 }}
                />
                <button
                  type="button"
                  aria-label="Remove minor"
                  onClick={() =>
                    setMinors((prev) => prev.filter((_, i) => i !== idx))
                  }
                  style={{
                    border: '1px solid rgba(0,0,0,0.2)',
                    background: 'transparent',
                    borderRadius: '8px',
                    padding: '0 0.7rem',
                    cursor: 'pointer',
                    color: '#9F1239',
                    fontSize: '1.1rem',
                    lineHeight: 1,
                  }}
                >
                  &times;
                </button>
              </div>
            ))}

            <button
              type="button"
              data-testid="add-minor"
              onClick={() =>
                setMinors((prev) => [...prev, { full_name: '', dob: '' }])
              }
              style={{
                background: 'transparent',
                color: '#0D2351',
                border: '1px solid rgba(13,35,81,0.3)',
                borderRadius: '8px',
                padding: '0.4rem 0.8rem',
                fontSize: '0.9rem',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              + Add a minor
            </button>
            {errors.minors ? (
              <p style={{ margin: '0.4rem 0 0', color: '#9F1239', fontSize: '0.8rem' }}>
                {errors.minors}
              </p>
            ) : null}

            {minors.length > 0 && minorAck ? (
              <label
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.6rem',
                  marginTop: '0.75rem',
                  padding: '0.6rem 0.75rem',
                  background: acks[minorAck.id] ? '#FFFBEB' : '#F9FAFB',
                  border: `1px solid ${
                    acks[minorAck.id] ? '#FCD34D' : 'rgba(0,0,0,0.12)'
                  }`,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.88rem',
                  color: 'rgba(0,0,0,0.85)',
                }}
              >
                <input
                  type="checkbox"
                  checked={!!acks[minorAck.id]}
                  data-testid={`waiver-ack-${minorAck.id}`}
                  onChange={(e) =>
                    setAcks((prev) => ({
                      ...prev,
                      [minorAck.id]: e.target.checked,
                    }))
                  }
                  style={{ marginTop: '0.15rem', flexShrink: 0 }}
                />
                <span>{minorAck.label}</span>
              </label>
            ) : null}
            {errors.minorAck ? (
              <p style={{ margin: '0.4rem 0 0', color: '#9F1239', fontSize: '0.8rem' }}>
                {errors.minorAck}
              </p>
            ) : null}
          </div>

          {errors.acks ? (
            <p style={{ margin: '0 0 0.75rem', color: '#9F1239', fontSize: '0.85rem' }}>
              {errors.acks}
            </p>
          ) : null}
          {errors.photo ? (
            <p style={{ margin: '0 0 0.75rem', color: '#9F1239', fontSize: '0.85rem' }}>
              {errors.photo}
            </p>
          ) : null}

          <h3 style={{ margin: '0 0 0.75rem', fontSize: '1.05rem', color: '#0D2351' }}>
            Sign electronically
          </h3>

          <TextField
            id="waiver-fullname"
            label="Full legal name (your electronic signature)"
            value={fullName}
            onChange={setFullName}
            error={errors.fullName}
            autoComplete="name"
            required
          />
          <TextField
            id="waiver-dob"
            label="Date of birth"
            type="date"
            value={dob}
            onChange={setDob}
            error={errors.dob}
            required
          />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '0 1rem',
            }}
          >
            <TextField
              id="waiver-emergency-name"
              label="Emergency contact name (optional)"
              value={emergencyName}
              onChange={setEmergencyName}
              autoComplete="off"
            />
            <TextField
              id="waiver-emergency-phone"
              label="Emergency contact phone (optional)"
              type="tel"
              value={emergencyPhone}
              onChange={setEmergencyPhone}
              autoComplete="off"
            />
          </div>

          {fullName.trim() ? (
            <p
              style={{
                margin: '0 0 1rem',
                fontFamily: '"Brush Script MT", "Segoe Script", cursive',
                fontStyle: 'italic',
                fontSize: '1.6rem',
                color: '#0D2351',
              }}
              aria-hidden="true"
            >
              {fullName.trim()}
            </p>
          ) : null}

          {submitError ? (
            <p
              role="alert"
              data-testid="waiver-error"
              style={{
                padding: '0.75rem 1rem',
                background: '#FEF2F2',
                color: '#9F1239',
                borderRadius: '8px',
                fontSize: '0.9rem',
                marginBottom: '1rem',
              }}
            >
              {submitError}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={submitting}
            data-testid="waiver-submit"
            style={{
              width: '100%',
              padding: '0.85rem 1.1rem',
              background: submitting ? 'rgba(13,35,81,0.5)' : '#0D2351',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '10px',
              fontSize: '1.05rem',
              fontWeight: 700,
              cursor: submitting ? 'wait' : 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {submitting ? 'Submitting…' : 'Sign & request reservation'}
          </button>
          <p
            style={{
              margin: '0.75rem 0 0',
              textAlign: 'center',
              fontSize: '0.8rem',
              color: 'rgba(0,0,0,0.55)',
            }}
          >
            By signing you agree to the waiver above. No charge today.
          </p>
        </form>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Confirmation — shown once the booking is created
// ---------------------------------------------------------------------------
function ConfirmationStep({ booking }) {
  return (
    <section
      style={{ maxWidth: '560px', margin: '0 auto', textAlign: 'center' }}
      data-testid="booking-confirmation"
    >
      <div
        aria-hidden="true"
        style={{
          width: '64px',
          height: '64px',
          margin: '1rem auto 1.25rem',
          borderRadius: '50%',
          background: '#0D2351',
          color: '#F6C300',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '2rem',
          fontWeight: 700,
          lineHeight: 1,
        }}
      >
        &#10003;
      </div>

      <h1 style={{ margin: 0, fontSize: '1.6rem', color: '#0D2351' }}>
        Reservation request received
      </h1>
      <p
        style={{
          marginTop: '0.5rem',
          fontSize: '1rem',
          color: 'rgba(0,0,0,0.7)',
        }}
      >
        Booking{' '}
        <strong data-testid="booking-number">{booking.booking_number}</strong>
      </p>

      <div
        style={{
          textAlign: 'left',
          padding: '1rem 1.25rem',
          background: '#F9FAFB',
          border: '1px solid rgba(0,0,0,0.08)',
          borderRadius: '12px',
          margin: '1.5rem 0',
        }}
      >
        <SummaryRow label="Trip" value={booking.trip_name} />
        <SummaryRow
          label="Date"
          value={booking.charter_date ? formatYmd(booking.charter_date) : null}
        />
        <SummaryRow label="Guests" value={booking.guest_count} />
        {booking.is_private ? (
          <SummaryRow
            label="Estimated total"
            value={`${PRIVATE_QUOTE_RANGE} · quoted*`}
          />
        ) : booking.total_cents != null ? (
          <SummaryRow
            label="Estimated total"
            value={`${formatUsd(booking.total_cents)}*`}
          />
        ) : null}
      </div>

      <div style={{ textAlign: 'left' }}>
        <h2 style={{ fontSize: '1.1rem', color: '#0D2351' }}>
          What happens next
        </h2>
        <ol
          style={{
            paddingLeft: '1.25rem',
            fontSize: '0.95rem',
            color: 'rgba(0,0,0,0.75)',
            lineHeight: 1.6,
          }}
        >
          <li>
            Captain Matt reviews your request and confirms the date and start
            time — usually within a day.
          </li>
          <li>
            You&rsquo;ll get a secure payment link (or a quick quote for custom
            trips and add-ons) by email
            {booking.email ? ` at ${booking.email}` : ''}.
          </li>
          <li>Once it&rsquo;s paid, you&rsquo;re locked in. That&rsquo;s it.</li>
        </ol>
        <p style={{ fontSize: '0.85rem', color: 'rgba(0,0,0,0.55)' }}>
          *Estimate based on the trip rate. Final total is confirmed before any
          charge.
        </p>
        <p style={{ fontSize: '0.95rem', color: 'rgba(0,0,0,0.75)' }}>
          Questions, or need to change something? Call or text Captain Matt at{' '}
          <a href="tel:+19042439044" style={{ color: '#0D2351', fontWeight: 700 }}>
            904-243-9044
          </a>
          .
        </p>
      </div>
    </section>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: '1rem',
        padding: '0.4rem 0',
        fontSize: '0.95rem',
      }}
    >
      <span style={{ color: 'rgba(0,0,0,0.6)' }}>{label}</span>
      <span style={{ fontWeight: 700, color: '#0D2351', textAlign: 'right' }}>
        {value || '—'}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SelectionBar — running summary of what's been chosen so far, with
// per-row Change links to jump back to that step.
// ---------------------------------------------------------------------------
function SelectionBar({ items }) {
  return (
    <ul
      style={{
        listStyle: 'none',
        margin: 0,
        padding: '0.5rem 0.85rem',
        background: '#F9FAFB',
        border: '1px solid rgba(0,0,0,0.06)',
        borderRadius: '8px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '1rem',
      }}
    >
      {items.map((item) => (
        <li
          key={item.label}
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: '0.5rem',
            fontSize: '0.85rem',
          }}
        >
          <span
            style={{
              fontWeight: 700,
              color: 'rgba(0,0,0,0.55)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontSize: '0.75rem',
            }}
          >
            {item.label}
          </span>
          <span style={{ fontWeight: 600, color: '#0D2351' }}>
            {item.value || '—'}
          </span>
          {item.onChange ? (
            <button
              type="button"
              onClick={item.onChange}
              style={{
                background: 'transparent',
                color: '#0D2351',
                border: 'none',
                padding: 0,
                fontSize: '0.8rem',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              Change
            </button>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function toYmd(date) {
  if (typeof date === 'string') return date;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatYmd(ymd) {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatDuration(minutes) {
  if (!minutes) return '';
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hrs && mins) return `${hrs}h ${mins}m`;
  if (hrs) return `${hrs}h`;
  return `${mins}m`;
}

function priceLabel(trip) {
  if (!trip) return '';
  if (trip.private_flat_rate_cents && !trip.price_per_person_cents) {
    return PRIVATE_QUOTE_LABEL;
  }
  if (trip.price_per_person_cents) {
    return `${formatUsd(trip.price_per_person_cents)}/person`;
  }
  return 'Call for pricing';
}

function formatUsd(cents) {
  return `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}
